/* @flow weak */
import { debounce, throttle } from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';

import AlertDialog from 'components/AlertDialog.jsx';
import ChatActions from 'chat/actions/ChatActions';
import ChatStore from 'chat/stores/ChatStore';
import GroupSelectionDialog from 'groups/components/GroupSelectionDialog.jsx';
import GroupStore from 'groups/stores/GroupStore';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import { mountModal } from 'lib/modal';
import focusNode from 'utils/focusNode';
import getMatcher from 'utils/getMatcher';
import linkStateWithStores from 'components/mixins/linkStateWithStores';

// Used to decrease duplicate code between NewConversationHeader and AddParticipantModal
const ChatParticipantSearchMixin = {
  mixins: [
    linkStateWithStores([ChatStore]),
  ],

  statics: {
    loadData() {
      // TODO handle this by Flux instead of action promise
      return ChatActions.promisePotentialChatMembers().then(() => ChatStore.getMembers());
    },
  },

  getInitialState() {
    return {
      members: ChatStore.getMembers(),
      allMembersLoaded: ChatStore.areAllMembersLoaded(),
      query: '',
      results: ChatStore.getMembers(),
      highlight: 0,
    };
  },

  getStateFromStores() {
    return {
      members: ChatStore.getMembers(),
      results: this.getResults(this.state.query),
      allMembersLoaded: ChatStore.areAllMembersLoaded(),
    };
  },

  _getInput() {
    return typeof this.getInput === 'function' ?
      this.getInput() :
      ReactDOM.findDOMNode(this.refs.memberSearch);
  },

  componentDidUpdate(prevProps, prevState) {
    if (prevState.members.length === 0 && this.state.members.length > 0) {
      this.performSearch();
    }
  },

  isScrolledToBottom() {
    const scrollable = this.refs.scrollArea || this._scrollableArea;
    return scrollable.isScrolledToBottom(100);
  },

  _blockResultMouseOverWhileScrolling: false,

  handleScroll: throttle(function() {
    if (!this.state.updating && this.isScrolledToBottom() && !this.state.areAllMembersLoaded) {
      this.setState({ updating: true });
      this.loadMoreMembers().then(() => { this.setState({ updating: false }); });
    }
  }, 100),

  loadMoreMembers() {
    return ChatActions.promisePotentialChatMembers(this.state.query);
  },

  performSearchOnInputChange: debounce(function() {
    this.performSearch();
    this.setState({ highlight: 0 });
  }, 100),

  performSearch() {
    const input = this._getInput();
    const value = input.value;

    this.setState({
      query: value,
      results: this.getResults(value),
      highlight: -1,
    }, this.loadMoreMembers);
  },

  getResults(query = '') {
    // Search the possible members for the query substring
    const isMatch = getMatcher(query);

    return this.state.members.filter(member => {
      return (
        isMatch([
          member.getName(),
          member.getFirstName(),
          member.getLastName(),
          member.getNote(),
          member.getGroupNames(),
        ])
      );
    });
  },

  memberNotEligible(member) {
    let modal;
    if (member.isAgeUnknown() || member.isPending()) {
      if (member.isAgeUnknown()) {
        ChatActions.requestMemberAge(member);
      }

      modal = (
        <AlertDialog
          title="We need to collect more information from the participant"
          subtext="We'll notify you as soon as they're available for two-way messaging."
        />
      );
    } else if (member.isUnder13()) {
      modal = (
        <AlertDialog
          title="Two-way messaging is only available for students 13 and older."
          subtext="Don't worry—they can still receive announcements."
        />
      );
    } else if (member.isUnavailable()) {
      modal = (
        <AlertDialog
          title={`Sorry! ${member.getName()} isn't accepting messages on Remind.`}
        />
      );
    }

    if (modal) {
      this.focusInput();
      this._preventBlur = true;
      mountModal(modal, this);
    }
  },

  focusInput() {
    focusNode(this._getInput());
  },

  handleInputChange() {
    this.performSearchOnInputChange();
  },

  handleResultMouseOver(index) {
    if (this._blockResultMouseOverWhileScrolling) {
      this._blockResultMouseOverWhileScrolling = false;
      return;
    }
    this.setState({ highlight: index });
  },

  renderGroupSelectionDialog() {
    const groups = GroupStore.getInvitableGroups();
    return (
      <GroupSelectionDialog
        ref="groupSelectionDialog"
        title="Select a class"
        groups={groups}
        onSelect={this.handleGroupSelect}
      />
    );
  },

  renderLoadingIndicator() {
    if (!ChatStore.areAllMembersLoaded()) {
      return (
        <div className="ChatList-loading">
          <LoadingIndicator size="small" />
        </div>
      );
    } else {
      return null;
    }
  },
};

export default ChatParticipantSearchMixin;



// WEBPACK FOOTER //
// ./src/components/mixins/ChatParticipantSearchMixin.js